import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Send, XCircle, CheckCircle, PenLine, Eye, Clock, BookOpen } from 'lucide-react';

const statusStyles = {
  draft:     'bg-stone-100 text-stone-600 border border-stone-300',
  review:    'bg-amber-100 text-amber-700 border border-amber-300',
  refused:   'bg-red-100 text-red-700 border border-red-300',
  approved:  'bg-emerald-100 text-emerald-700 border border-emerald-300',
  published: 'bg-blue-600 text-white border border-blue-600',
};

const statCards = [
  { key: 'review',    label: 'In Review', icon: Send,          color: 'text-amber-600' },
  { key: 'refused',   label: 'Refused',   icon: XCircle,       color: 'text-red-500' },
  { key: 'approved',  label: 'Approved',  icon: CheckCircle,   color: 'text-emerald-600' },
  { key: 'published', label: 'Published', icon: BookOpen,      color: 'text-blue-600' },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ counts: {}, total: 0, recent: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-foreground font-outfit tracking-tight" data-testid="dashboard-title">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">Here's your editorial overview</p>
        </div>
        <Button
          data-testid="new-story-btn"
          onClick={() => navigate('/stories/new')}
          className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
        >
          <PenLine size={16} className="mr-2" /> New Story
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {statCards.map(({ key, label, icon: Icon, color }) => (
          <Card
            key={key}
            className="bg-card border-border hover:border-[#E7D2B8] hover:shadow-sm transition-all cursor-pointer"
            onClick={() => navigate(`/stories?status=${key}`)}
            data-testid={`stat-card-${key}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">{label}</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {loading ? '—' : (stats.counts[key] || 0)}
                  </p>
                </div>
                <div className={`w-11 h-11 rounded-lg bg-[#F8EBD9] flex items-center justify-center ${color}`}>
                  <Icon size={22} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-foreground font-outfit">Recent Stories</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground hover:bg-[#FFE4C4]/60"
              onClick={() => navigate('/stories')}
              data-testid="view-all-stories"
            >
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-[#F8EBD9] rounded-lg animate-pulse" />
              ))}
            </div>
          ) : stats.recent?.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto text-[#E7D2B8] mb-3" size={40} />
              <p className="text-muted-foreground">No stories yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {stats.recent?.map(story => (
                <div
                  key={story.id}
                  onClick={() => navigate(`/stories/${story.id}`)}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-[#FFF4E8] cursor-pointer transition-colors group"
                  data-testid={`recent-story-${story.id}`}
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm font-medium text-foreground truncate group-hover:text-blue-600 transition-colors">
                      {story.headline || 'Untitled'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {story.client_name}{story.project_name && ` / ${story.project_name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge className={`text-xs capitalize ${statusStyles[story.status] || ''}`}>
                      {story.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock size={12} />{new Date(story.updated_at).toLocaleDateString()}
                    </span>
                    <Eye size={16} className="text-[#E7D2B8] group-hover:text-muted-foreground transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}